import { useCallback, useEffect, useMemo } from 'react';
import { Api } from '@monorepo/shared/apiClient';
import { useToast } from '@monorepo/shared/contexts';
import debounce from 'lodash.debounce';
import isEqual from 'lodash.isequal';
import { useMutation } from 'react-query';
import { getCache, invalidateCache, setCache } from './useUserSettings';

type UseSaveUserSettingsParams<K extends Api.SupportedUserSettingsKey> = {
  debounceMs?: number;
  errorMessage?: string;
  key: K;
  projectId?: string;
};

export function useSaveUserSettings<K extends Api.SupportedUserSettingsKey>({
  debounceMs,
  errorMessage = 'Unable to save settings.',
  key: settingsKey,
  projectId,
}: UseSaveUserSettingsParams<K>) {
  const { showUserFriendlyErrorToast } = useToast();

  const [saveSettings] = useMutation<
    Api.UserSettingsValueForKey<K>,
    Api.ErrorResponse,
    Api.SaveUserSettingsParams<K>
  >(Api.saveUserSettingsByKey, {
    onError: (error, variables) => {
      showUserFriendlyErrorToast(error, errorMessage);
      const cachedValue = getCache(variables);

      // A newer setSettings may have replaced the failed value already; refetching
      // here would discard it in favor of stale server data.
      if (isEqual(cachedValue, variables.value)) {
        void invalidateCache(variables);
      }
    },
  });

  const debouncedSaveSettings = useMemo(
    () =>
      debounceMs
        ? debounce((params: Api.SaveUserSettingsParams<K>) => {
            void saveSettings(params);
          }, debounceMs)
        : undefined,
    [debounceMs, saveSettings],
  );

  useEffect(
    () => () => {
      debouncedSaveSettings?.flush();
    },
    [debouncedSaveSettings, projectId, settingsKey],
  );

  return useCallback(
    (value: Api.UserSettingsValueForKey<K>) => {
      if (!projectId) {
        return;
      }
      setCache({ projectId, key: settingsKey }, value);
      const params = { projectId, key: settingsKey, value };
      if (debouncedSaveSettings) {
        debouncedSaveSettings(params);
      } else {
        void saveSettings(params);
      }
    },
    [debouncedSaveSettings, projectId, saveSettings, settingsKey],
  );
}
